import { z } from 'zod';
import '@apexdesigner/schema-tools';
import {
  belongsTo,
  hasMany,
  options,
  references,
} from '@apexdesigner/schema-tools';

export const serviceTask = z
  .object({
    id: z.uuid().hidden().describe('Unique identifier'),

    referenceNumber: z
      .number()
      .nullable()
      .optional()
      .describe('Human-readable reference number'),

    name: z
      .string()
      .nullable()
      .optional()
      .describe('Optional name for this task'),

    queue: z
      .string()
      .nullable()
      .optional()
      .describe('Queue where this task is processed'),

    status: options([
      { value: 'Active', name: 'Active' },
      { value: 'Paused', name: 'Paused' },
      { value: 'Complete', name: 'Complete' },
      { value: 'Canceled', name: 'Canceled' },
      { value: 'Error', name: 'Error' },
    ])
      .nullable()
      .optional()
      .describe('Current state of this task'),

    attempts: z
      .number()
      .nullable()
      .optional()
      .describe('Number of times execution was attempted'),

    claimedBy: z
      .string()
      .nullable()
      .optional()
      .hidden()
      .describe('Service processing this task'),

    claimedAt: z.coerce
      .date()
      .nullable()
      .optional()
      .describe('When processing started on this task'),

    errorMessage: z
      .string()
      .nullable()
      .optional()
      .presentAs('textarea')
      .describe('Error details if the task failed'),

    errorCode: z
      .string()
      .nullable()
      .optional()
      .describe('Stable error code if the task failed with a known error'),

    data: z
      .unknown()
      .nullable()
      .optional()
      .presentAs('json')
      .describe('Input and working data for this task'),

    completedAt: z.coerce
      .date()
      .nullable()
      .optional()
      .describe('When this task was finished'),

    loopIndex: z
      .number()
      .nullable()
      .optional()
      .describe('Loop index for multi-instance activities'),

    resultApplied: z
      .boolean()
      .nullable()
      .optional()
      .hidden()
      .describe(
        "Whether this iteration's completion has been folded into the process instance (before-complete hook and output mappings)",
      ),

    createdAt: z.coerce
      .date()
      .nullable()
      .optional()
      .hidden()
      .describe('createdAt'),

    updatedAt: z.coerce
      .date()
      .nullable()
      .optional()
      .hidden()
      .describe('updatedAt'),

    tokenId: z.uuid().hidden().describe('Foreign key to Token'),

    tenantId: z
      .uuid()
      .nullable()
      .optional()
      .presentAs('reference')
      .describe('Foreign key to Tenant'),

    token: belongsTo('Token'),

    serviceTaskHistories: hasMany('ServiceTaskHistory'),

    tenant: references('Tenant'),
  })
  .describe('A task that is processed by an automated service')
  .as('ServiceTask');

export { serviceTask as serviceTaskSchema };
